/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import docAPI from "api";
import { pagesInView, getTileCanvasRefPerPage, getTileCanvasRef } from "selectors/pagedViewSelectors";
import { getPageCoordinates } from "selectors/pageNumber";
import { getHCColorPallete } from "selectors/highContrastSelectors";
import { getPageAccText } from "selectors/pageSelectors";
import { API_TEXT_EXTRACT_PRIORITY } from "constants/constants";
import { drawUpdatedRect, getComputedTileRect } from "../utils/TileCanvasUtils";
import docApi from "api";

export const getPageApis = stores => {
  function getPageRendition(messageObj) {
    const {
      itemType = "page",
      zoomLevel,
      index,
      devicePixelRatio,
      priority,
      type = "page",
      showAnnots,
      tileRect,
      isPreload = false
    } = messageObj;
    const pagesInViewObj = pagesInView(stores);
    let response = docAPI.getRendition(
      itemType,
      index,
      zoomLevel,
      pagesInViewObj.start,
      pagesInViewObj.end,
      devicePixelRatio,
      priority,
      { type, tileRect, showAnnots },
      isPreload,
      getHCColorPallete(stores)
    );

    if (!(response instanceof Promise)) {
      if (response && response.ajs instanceof Promise) {
        response = response.ajs;
        return response;
      }
      return response;
    }
  }

  function drawRectOnTileCanvas(messageObj) {
    const { index, devicePixelRatio, tileRect, zoomLevel } = messageObj;
    const computedTileRect = getComputedTileRect(tileRect, zoomLevel, index);
    const getPageRenditionPromise = getPageRendition({ ...messageObj, tileRect: computedTileRect });
    getPageRenditionPromise.then(resp => {
      stores.pagedViewState.setIsTileCanvasLoaded(true);
      drawUpdatedRect(
        stores,
        index,
        {
          top: tileRect.top * devicePixelRatio,
          bottom: tileRect.bottom * devicePixelRatio,
          left: tileRect.left * devicePixelRatio,
          right: tileRect.right * devicePixelRatio
        },
        resp.data.buffer,
        resp.data.mimeType
      );
    });

    return getPageRenditionPromise;
  }

  function clearTileCanvas() {
    getTileCanvasRef(stores).forEach((value, key) => {
      const editCanvasRef = getTileCanvasRefPerPage(stores, key);
      editCanvasRef.current = null;
    });
    stores.pagedViewState.setIsTileCanvasLoaded(false);
  }

  async function extractPageText(index) {
    await stores.documentStore.loadPageAccText(index, API_TEXT_EXTRACT_PRIORITY, { wordRects: true });
    return getPageAccText(stores, index);
  }

  function getPageDetails(eventObj) {
    const { pageX, pageY } = eventObj;
    return getPageCoordinates(stores, pageX, pageY);
  }

  function reloadPages(startPageNum, endPageNum, options = {}) {
    return stores.documentStore.reloadPagesAndGeometries(startPageNum, endPageNum, options);
  }

  return {
    getPageRendition,
    extractPageText,
    getPageDetails,
    drawRectOnTileCanvas,
    clearTileCanvas,
    reloadPages,
    getBBoxForPage: docApi.getBBoxForPage.bind(docApi),
    getBBoxContent: docApi.getBBoxContent.bind(docApi)
  };
};
